import { inject } from '@angular/core';
import { Route, Router } from '@angular/router';
import { environment } from '../environments/environment';
import { MainLayoutComponent } from './layout/main-layout'; // check import path
import { DashboardComponent } from './features/dashboard/dashboard'; // check import path
import { permissionGuard } from './core/guards/permission.guard';
import { authGuard } from './core/guards/auth.guard';

export const appRoutes: Route[] = [
    {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login').then(m => m.LoginComponent)
    },
    {
        path: '',
        component: MainLayoutComponent,
        canActivate: [authGuard],
        canActivateChild: [authGuard],
        children: [
            {
                path: 'management',
                data: { label: '管理儀錶板', icon: 'ci ci-dashboard !text-[oklch(0.5577_0.1247_254.82)]' },
                children: [
                    {
                        path: 'dashboard',
                        component: DashboardComponent,
                        canActivate: [permissionGuard],
                        data: { label: '儀錶板', requiredPermission: '儀錶板_view' }
                    },
                    {
                        path: 'analysis',
                        loadComponent: () => import('./features/electricity-analysis/electricity-analysis').then(m => m.ElectricityAnalysisComponent),
                        canActivate: [permissionGuard],
                        data: { label: '用電分析', requiredPermission: '用電分析_view' }
                    },
                ]
            },
            {
                path: 'alerts',
                loadComponent: () => import('./features/alert-history/alert-history').then(m => m.AlertHistoryComponent),
                canActivate: [permissionGuard],
                data: { label: '告警紀錄', icon: 'ci ci-alert !text-[oklch(0.6483_0.1855_22.78)]', requiredPermission: '告警管理_view' }
            },
            {
                path: 'devices',
                data: { label: '設備管理', icon: 'ci ci-device-setting !text-[oklch(0.6625_0.1276_132.07)]' },
                children: [
                    {
                        path: 'list',
                        loadComponent: () => import('./features/device-list/device-list').then(m => m.DeviceListComponent),
                        canActivate: [permissionGuard],
                        data: { label: '設備列表', requiredPermission: '設備管理_view' }
                    },
                    {
                        path: 'detail/:id',
                        loadComponent: () => import('./features/device-detail/device-detail-layout').then(m => m.DeviceDetailLayoutComponent),
                        data: { label: '設備詳情', hidden: true },
                        children: [
                            { path: '', redirectTo: 'basic-info', pathMatch: 'full' },
                            { path: 'basic-info', loadComponent: () => import('./features/device-detail/basic/device-detail-basic').then(m => m.DeviceDetailBasicComponent), data: { label: '基本資料' } },
                            { path: 'points', loadComponent: () => import('./features/device-detail/points/device-detail-points.component').then(m => m.DeviceDetailPointsComponent), data: { label: '點位管理' } },
                            { path: 'maintenance', loadComponent: () => import('./features/device-detail/maintenance/device-detail-maintenance.component').then(m => m.DeviceDetailMaintenanceComponent), data: { label: '維保紀錄' } },
                            { path: 'trends', loadComponent: () => import('./features/device-detail/points-trends/device-detail-points-trends.component').then(m => m.DeviceDetailPointsTrendsComponent), data: { label: '點位趨勢' } },
                            { path: 'history-trends', loadComponent: () => import('./features/device-detail/points-history-trends/device-detail-points-history-trends.component').then(m => m.DeviceDetailPointsHistoryTrendsComponent), data: { label: '歷史趨勢' } }
                        ]
                    },
                    {
                        path: 'schedule',
                        loadComponent: () => import('./features/device-schedule/device-schedule').then(m => m.DeviceScheduleComponent),
                        canActivate: [permissionGuard],
                        data: { label: '排程設定', requiredPermission: '設備排程管理_view' }
                    }
                ]
            },
            {
                path: 'system',
                data: { label: '系統管理', icon: 'ci ci-system-setting !text-[oklch(0.7223_0.1062_194.28)]' },
                children: [
                    { path: '', redirectTo: 'account', pathMatch: 'full' },
                    {
                        path: 'account',
                        loadComponent: () => import('./features/system/account/account-management').then(m => m.AccountManagementComponent),
                        canActivate: [permissionGuard],
                        data: { label: '帳號管理', requiredPermission: '系統管理_view' }
                    },
                    {
                        path: 'role',
                        canActivate: [permissionGuard],
                        data: { label: '角色管理', requiredPermission: '系統管理_view' },
                        children: [
                            { path: '', loadComponent: () => import('./features/system/role/role-management').then(m => m.RoleManagementComponent) },
                            { path: 'add', loadComponent: () => import('./features/system/role/role-form/role-form').then(m => m.RoleFormComponent) },
                            { path: 'edit/:id', loadComponent: () => import('./features/system/role/role-form/role-form').then(m => m.RoleFormComponent) }
                        ]
                    },
                    {
                        path: 'operation',
                        loadComponent: () => import('./features/system/operation/operation-management').then(m => m.OperationManagementComponent),
                        canActivate: [permissionGuard],
                        data: { label: '操作管理', requiredPermission: '系統管理_view' },
                        children: [
                            { path: '', redirectTo: 'login', pathMatch: 'full' },
                            { path: 'login', loadComponent: () => import('./features/system/operation/login-log/login-log').then(m => m.LoginLogComponent) },
                            { path: 'record', loadComponent: () => import('./features/system/operation/operation-log/operation-log').then(m => m.OperationLogComponent) },
                            { path: 'permission', loadComponent: () => import('./features/system/operation/permission-log/permission-log').then(m => m.PermissionLogComponent) }
                        ]
                    },
                    {
                        path: 'smtp',
                        loadComponent: () => import('./features/system/smtp/smtp-setting').then(m => m.SmtpSettingComponent),
                        canActivate: [permissionGuard, () => environment.showSmtpSettings || inject(Router).parseUrl('/404')],
                        data: {
                            label: 'SMTP設定',
                            requiredPermission: '系統管理_view',
                            hidden: !environment.showSmtpSettings
                        }
                    }
                ]
            },
            {
                path: 'notification',
                loadComponent: () => import('./features/notification/notification-management').then(m => m.NotificationManagementComponent),
                canActivate: [permissionGuard],
                data: { label: '通知管理', icon: 'ci ci-notification !text-[oklch(0.7931_0.165852_77.1204)]', requiredPermission: '通知管理_view' }
            },
            { path: '', redirectTo: 'management/dashboard', pathMatch: 'full' }
        ]
    },
    {
        path: '404',
        loadComponent: () => import('./features/not-found/not-found').then(m => m.NotFoundComponent)
    },
    {
        path: '**',
        redirectTo: '404'
    }
];
